import { ref } from 'vue'
import { defineStore } from 'pinia'
import { doc, onSnapshot, type Unsubscribe } from 'firebase/firestore'
import { db } from '@/firebase'
import type { ActionKey, AppRole, PermissionConfig } from '@/types'

// Mirrors the Python _DEFAULTS — used as offline fallback before Firestore arrives
const DEFAULTS: PermissionConfig = {
  'incident.create_close':   ['Incident Commander'],
  'incident.edit':           ['Incident Commander', 'Operations Section Chief', 'Logistics'],
  'op.create':               ['Incident Commander'],
  'team.create_manage':      ['Incident Commander', 'Operations Section Chief'],
  'team.update_status':      ['Incident Commander', 'Operations Section Chief', 'Team Leader'],
  'assignment.create':       ['Incident Commander', 'Operations Section Chief'],
  'assignment.debrief':      ['Incident Commander', 'Operations Section Chief', 'Team Leader'],
  'personnel.checkin':       ['Incident Commander', 'Logistics'],
  'personnel.checkout_others': ['Incident Commander', 'Operations Section Chief', 'Operations', 'Logistics', 'General Overhead'],
  'forms.edit':              ['Incident Commander', 'Operations Section Chief', 'Logistics'],
  'forms.view':              ['Incident Commander', 'Operations Section Chief', 'Team Leader', 'Team Member', 'Logistics', 'admin', 'member'],
  'clues.log':               ['Incident Commander', 'Operations Section Chief', 'Team Leader', 'Team Member'],
  'forms.export_pdf':        ['Incident Commander', 'Operations Section Chief', 'Logistics'],
  'audit.view':              ['Incident Commander', 'Operations Section Chief', 'Logistics'],
  'stats.view':              ['Incident Commander', 'admin'],
  'agency.logo_upload':      ['admin'],
  'personnel.generate_qr':   ['Incident Commander', 'Logistics'],
  'personnel.invite':        ['Incident Commander', 'Logistics'],
  'subjects.generate_flyer': ['Incident Commander', 'Logistics'],
  'radio_log.log_entry':     ['Incident Commander', 'Operations Section Chief', 'Team Leader', 'Logistics'],
  'radio_log.edit_entry':    ['Incident Commander', 'Operations Section Chief'],
  'radio_log.delete_entry':  ['Incident Commander', 'Operations Section Chief'],
  'radio_log.export_ics309': ['Incident Commander', 'Operations Section Chief', 'Logistics'],
  'iwi.declare':                    ['Incident Commander', 'Operations Section Chief', 'Logistics', 'Team Leader'],
  'iwi.resolve':                    ['Incident Commander', 'Operations Section Chief'],
  'iwi.checklist':                  ['Incident Commander', 'Operations Section Chief', 'Logistics'],
  'priority_traffic.create':        ['Incident Commander', 'Operations Section Chief', 'Logistics', 'Communications', 'Team Leader'],
  'priority_traffic.resolve':       ['Incident Commander', 'Operations Section Chief', 'Logistics'],
}

export const usePermissionsStore = defineStore('permissions', () => {
  const config = ref<PermissionConfig>({ ...DEFAULTS })
  let _unsub: Unsubscribe | null = null

  /**
   * Call once in App.vue after auth is ready. Keeps config live via onSnapshot.
   */
  function subscribe() {
    if (_unsub) return
    _unsub = onSnapshot(
      doc(db, 'platform', 'permissions'),
      (snap) => {
        if (snap.exists()) {
          const data = snap.data() as Partial<PermissionConfig>
          config.value = { ...DEFAULTS, ...data }
        }
        // If document doesn't exist yet, keep defaults
      },
      () => {
        // Error (e.g. offline at first load) — keep defaults / last cached value
      },
    )
  }

  function unsubscribe() {
    _unsub?.()
    _unsub = null
  }

  /**
   * Returns true if *role* is in the allowed list for *action*.
   * Callers choose whether to pass the agency role or operational role.
   */
  function can(action: ActionKey, role: AppRole | null | undefined): boolean {
    if (!role) return false
    const allowed = config.value[action] ?? DEFAULTS[action] ?? []
    return allowed.includes(role)
  }

  /**
   * Returns the list of roles that are allowed to perform *action*.
   * Used to build permission-denied tooltip messages.
   */
  function requiredRolesFor(action: ActionKey): string[] {
    return config.value[action] ?? DEFAULTS[action] ?? []
  }

  return { config, subscribe, unsubscribe, can, requiredRolesFor }
})
